var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { isUnderage } from '@teliaee/crm-components';
import { action, computed, observable, makeObservable } from 'mobx';
import { PersonalCodeError, Spinner } from '../config/common';
import { MandateQueryType, MandateType } from '../config/mandate';
import CustomerMandate from '../config/mandate/CustomerMandate';
import Mandate from '../config/mandate/Mandate';
import API from '../util/ApiUtil';
import CountryUtil from '../util/CountryUtil';
import { diff } from '../util/DiffUtil';
import { DEFAULT_VALID_UNTIL } from './MandateStore';
import { endOfDay } from 'date-fns';
export default class AddMandateStore {
    constructor(rootStore) {
        Object.defineProperty(this, "newCustomerMandate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new CustomerMandate({})
        });
        Object.defineProperty(this, "isCustomer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "validUntil", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "mandateExists", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "personNotFound", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "validatePersonFields", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isMandateForSelf", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "mandateSaveError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isModalOpen", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isUnderage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "CUSTOMER_MANDATE_FIELDS_TO_REFRESH", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ['firstName', 'lastName', 'communicationLanguageCode', 'birthday']
        });
        Object.defineProperty(this, "ALLOWED_PERSON_NAME_MISMATCHES", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 2
        });
        Object.defineProperty(this, "ssoStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "mandateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "spinnerStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "personDataRegistryStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "fetchPersonDataWhenValid", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                this.resetValidationState();
                this.personDataRegistryStore.validatePersonalCode(this.newCustomerMandate.personalCode, this.newCustomerMandate.personalCodeCountryCode);
                const isValid = this.personDataRegistryStore.isPersonalCodeValid &&
                    this.validateMandatePersonAuth() &&
                    this.validateMandateExistance() &&
                    this.newCustomerMandate.isPersonValid;
                if (isValid) {
                    await this.personDataRegistryStore
                        .fetchPersonData(this.newCustomerMandate.personalCode, this.newCustomerMandate.personalCodeCountryCode)
                        .then(this.handlePersonDataResponse);
                    this.enablePersonFieldValidation();
                }
            }
        });
        Object.defineProperty(this, "customerMandateExists", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return (this.mandateStore
                    .getResultValue(MandateQueryType.CUSTOMER_MANDATES)
                    .find((customerMandate) => customerMandate.isVisible(this.ssoStore.customer) &&
                    customerMandate.personalCode === this.newCustomerMandate.personalCode &&
                    customerMandate.personalCodeCountryCode === this.newCustomerMandate.personalCodeCountryCode) !== undefined);
            }
        });
        Object.defineProperty(this, "handlePersonDataResponse", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const personData = this.personDataRegistryStore.personMap.get(this.newCustomerMandate.personalCodeCountryCode + this.newCustomerMandate.personalCode);
                if (personData && this.personDataMatchesWithForm(personData)) {
                    this.refreshCustomerMandate(personData);
                    this.personNotFound = false;
                    this.setIsUnderAge();
                }
                else if (CountryUtil.isEstonia(this.newCustomerMandate.personalCodeCountryCode)) {
                    this.personNotFound = true;
                }
            }
        });
        Object.defineProperty(this, "personDataMatchesWithForm", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (personData) => {
                const personDataFullName = personData.firstName + personData.lastName;
                const newCustomerMandateFullName = this.newCustomerMandate.firstName + this.newCustomerMandate.lastName;
                return diff(newCustomerMandateFullName, personDataFullName) <= this.ALLOWED_PERSON_NAME_MISMATCHES;
            }
        });
        Object.defineProperty(this, "refreshCustomerMandate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (personData) => {
                this.CUSTOMER_MANDATE_FIELDS_TO_REFRESH.forEach((field) => this.setCustomerMandateField(personData, this.newCustomerMandate, field));
                this.isCustomer = personData.isCustomer;
            }
        });
        Object.defineProperty(this, "setCustomerMandateField", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (person, targetCustomerMandate, field) => {
                targetCustomerMandate[field] = person[field] || targetCustomerMandate[field];
            }
        });
        Object.defineProperty(this, "toggleMandateType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                if (this.removeMandate(value)) {
                    if (value === MandateType.BILLING) {
                        this.removeMandate(MandateType.SERVICE_USAGE_DISTINCTION);
                    }
                }
                else {
                    this.addMandate(value);
                    if (value === MandateType.BILLING) {
                        this.addMandate(MandateType.SERVICE_USAGE_DISTINCTION);
                    }
                }
            }
        });
        Object.defineProperty(this, "addMandate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                this.newCustomerMandate.mandates.push(new Mandate({ type: value, validFrom: new Date(), validUntil: this.validUntil }));
            }
        });
        Object.defineProperty(this, "removeMandate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                const mandateIndex = this.newCustomerMandate.mandates.findIndex((mandate) => mandate.type === value);
                if (mandateIndex > -1) {
                    return this.newCustomerMandate.mandates.splice(mandateIndex, 1);
                }
            }
        });
        Object.defineProperty(this, "changeValidUntil", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value = null) => {
                this.validUntil = endOfDay(value);
                this.newCustomerMandate.mandates.forEach((mandate) => {
                    mandate.validUntil = this.validUntil;
                });
            }
        });
        Object.defineProperty(this, "resetCustomerMandate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.newCustomerMandate = new CustomerMandate({});
                this.validUntil = DEFAULT_VALID_UNTIL;
                this.resetValidationState();
                this.toggleModalOpen(false);
            }
        });
        Object.defineProperty(this, "resetValidationState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.isCustomer = false;
                this.mandateExists = false;
                this.mandateSaveError = false;
                this.validatePersonFields = false;
                this.personNotFound = false;
                this.personDataRegistryStore.personalCodeError = null;
                this.isUnderage = false;
            }
        });
        Object.defineProperty(this, "submitCustomerMandate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                await this.saveCustomerMandate();
                return !this.mandateSaveError;
            }
        });
        Object.defineProperty(this, "saveCustomerMandate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                try {
                    this.spinnerStore.show(Spinner.ADD_MANDATE_MODAL);
                    await API.post('/mandate/customer-mandate', this.newCustomerMandate);
                    this.resetCustomerMandate();
                }
                catch (_a) {
                    this.handleSaveError();
                }
                finally {
                    this.spinnerStore.hide(Spinner.ADD_MANDATE_MODAL);
                }
            }
        });
        Object.defineProperty(this, "handleSaveError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.mandateSaveError = true;
            }
        });
        Object.defineProperty(this, "enablePersonFieldValidation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.validatePersonFields = true;
            }
        });
        Object.defineProperty(this, "hasMandateType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (mandateType) => {
                return !!this.newCustomerMandate.mandates.find((mandate) => mandate.type === mandateType);
            }
        });
        Object.defineProperty(this, "getMandateTypes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const mandateTypes = [MandateType.SERVICE, MandateType.BILLING];
                if (this.newCustomerMandate.mandates.find((mandate) => mandate.type === MandateType.BILLING)) {
                    mandateTypes.push(MandateType.SERVICE_USAGE_DISTINCTION);
                }
                return mandateTypes;
            }
        });
        Object.defineProperty(this, "toggleModalOpen", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (value) => {
                this.isModalOpen = value;
            }
        });
        makeObservable(this);
        this.mandateStore = rootStore.mandateStore;
        this.ssoStore = rootStore.ssoStore;
        this.personDataRegistryStore = rootStore.personDataRegistryStore;
        this.spinnerStore = rootStore.spinnerStore;
        this.validUntil = DEFAULT_VALID_UNTIL;
    }
    setIsUnderAge() {
        this.isUnderage = isUnderage(this.newCustomerMandate.personalCode);
    }
    validateMandateExistance() {
        if (this.customerMandateExists()) {
            this.personDataRegistryStore.personalCodeError = PersonalCodeError.PERSON_EXISTS;
            this.mandateExists = true;
            return false;
        }
        return true;
    }
    validateMandatePersonAuth() {
        this.isMandateForSelf =
            this.ssoStore.person &&
                this.newCustomerMandate.personalCode === this.ssoStore.person.personalCode &&
                this.newCustomerMandate.personalCodeCountryCode === this.ssoStore.person.personalCodeCountryCode;
        return !this.isMandateForSelf;
    }
    get canAddMandate() {
        return this.newCustomerMandate.isValid() && !!this.validUntil && !this.personNotFound && !this.isMandateForSelf && !this.mandateExists && !this.isUnderage;
    }
}
__decorate([
    observable
], AddMandateStore.prototype, "newCustomerMandate", void 0);
__decorate([
    observable
], AddMandateStore.prototype, "isCustomer", void 0);
__decorate([
    observable
], AddMandateStore.prototype, "validUntil", void 0);
__decorate([
    observable
], AddMandateStore.prototype, "mandateExists", void 0);
__decorate([
    observable
], AddMandateStore.prototype, "personNotFound", void 0);
__decorate([
    observable
], AddMandateStore.prototype, "validatePersonFields", void 0);
__decorate([
    observable
], AddMandateStore.prototype, "isMandateForSelf", void 0);
__decorate([
    observable
], AddMandateStore.prototype, "mandateSaveError", void 0);
__decorate([
    observable
], AddMandateStore.prototype, "isModalOpen", void 0);
__decorate([
    observable
], AddMandateStore.prototype, "isUnderage", void 0);
__decorate([
    action.bound
], AddMandateStore.prototype, "setIsUnderAge", null);
__decorate([
    action.bound
], AddMandateStore.prototype, "validateMandateExistance", null);
__decorate([
    action.bound
], AddMandateStore.prototype, "validateMandatePersonAuth", null);
__decorate([
    action.bound
], AddMandateStore.prototype, "handlePersonDataResponse", void 0);
__decorate([
    action.bound
], AddMandateStore.prototype, "refreshCustomerMandate", void 0);
__decorate([
    computed
], AddMandateStore.prototype, "canAddMandate", null);
__decorate([
    action.bound
], AddMandateStore.prototype, "toggleMandateType", void 0);
__decorate([
    action.bound
], AddMandateStore.prototype, "changeValidUntil", void 0);
__decorate([
    action.bound
], AddMandateStore.prototype, "resetCustomerMandate", void 0);
__decorate([
    action.bound
], AddMandateStore.prototype, "resetValidationState", void 0);
__decorate([
    action.bound
], AddMandateStore.prototype, "saveCustomerMandate", void 0);
__decorate([
    action.bound
], AddMandateStore.prototype, "handleSaveError", void 0);
__decorate([
    action.bound
], AddMandateStore.prototype, "enablePersonFieldValidation", void 0);
__decorate([
    action.bound
], AddMandateStore.prototype, "toggleModalOpen", void 0);
